import ElementPlus from "element-plus";
import "element-plus/dist/index.css";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import { debounce } from "lodash";
import { createI18n } from "vue-i18n";
import nl from "../locales/nl";
import nlElement from "element-plus/es/locale/lang/nl";
import type { App } from "vue";

/**
 * Registers ElementPlus components and icons globally in the Vue app.
 * @param {Object} app - The Vue app instance.
 */
function initializeElementPlus(app: App): void {
  // Register all ElementPlus icons globally
  for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component);
  }

  // Register ElementPlus globally
  app.use(ElementPlus, {
    locale: nlElement,
  });
}

/**
 * Sets up i18n for internationalization with default and fallback locales.
 * @returns {Object} The initialized i18n instance.
 */
function initializeI18n(app: App): void {
  const i18n = createI18n({
    locale: "nl",
    fallbackLocale: "nl",
    messages: {
      nl,
    },
  });
  app.use(i18n);
}

/**
 * Overrides the window.ResizeObserver with a debounced version to improve performance.
 */
function initializeResizeObserver(): void {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      // Debounce the callback with a delay of 5ms to reduce reflows.
      super(debounce(callback, 5));
    }
  };
}

/**
 * Performs all app-level setups including ElementPlus, ResizeObserver, and i18n.
 * @param {Object} app - The Vue app instance.
 */
export function setupApp(app: App): void {
  // Initialize ElementPlus and its icons
  initializeElementPlus(app);

  // Initialize the debounced ResizeObserver
  initializeResizeObserver();

  // Initialize i18n and use it in the Vue app
  initializeI18n(app);
}
